// Where the deceased's last marriage or civil union was dissolved, as the
// Québec DEC asks it: in Québec, outside Québec, or unknown. Stored in
// clients.marriage_dissolution_location (CHECK-constrained to these three).
// The DEC XML has no element for it — staff select it on the DEC web form.
//
// Before T-152 onboarding asked for a typed place instead, kept in
// clients.place_of_separation; it is shown as a hint when the new value is blank.

export const DISSOLUTION_LOCATION_PATH = 'deceased_client.marriage_dissolution_location';

export const DISSOLUTION_LOCATION_CHOICES = [
  { id: 'quebec', name: 'In Québec' },
  { id: 'outside_quebec', name: 'Outside Québec' },
  { id: 'unknown', name: 'Unknown' },
] as const;

export const dissolutionLocationLabel = (value: unknown): string | null =>
  DISSOLUTION_LOCATION_CHOICES.find((c) => c.id === value)?.name ?? null;

/** The old typed answer, worded for staff; null when there is none. */
export const legacyDissolutionHint = (client: { place_of_separation?: string | null; [key: string]: unknown } | null | undefined): string | null => {
  const place = client?.place_of_separation?.trim();
  if (!place) return null;
  if (place.toUpperCase() === 'UNKNOWN') return "Old onboarding answer: family didn't know the place";
  return `Old onboarding answer (typed place): ${place}`;
};

const WEB_FORM_SELECTION: Record<string, string> = {
  quebec: 'Marriage dissolved in Québec',
  outside_quebec: 'Marriage dissolved outside Québec',
  unknown: 'Unknown',
};

/**
 * The note shown beside "Download DEC XML" when the DEC asks where the
 * marriage was dissolved (Divorced / Widowed / Legally Separated). The XML has
 * no element for it (Thanatologues.xsd), so staff select it on the web form.
 * `isUnknown` is the validation row's flag for an "Unknown" answer.
 */
export const dissolutionDecNote = (
  value: unknown,
  isUnknown = false,
): { severity: 'info' | 'warning'; text: string } => {
  const selection = isUnknown ? WEB_FORM_SELECTION.unknown : WEB_FORM_SELECTION[value as string];
  if (selection) {
    return {
      severity: 'info',
      text: `On the DEC web form, select: ${selection}. The XML can't carry this answer.`,
    };
  }
  return {
    severity: 'warning',
    text: "Where the marriage was dissolved is missing (In Québec / Outside Québec / Unknown). The DEC web form asks for it and the XML can't carry it, so get the family's answer before you do the DEC.",
  };
};
